import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/firebaseConfig';
import { collection, addDoc, doc, updateDoc } from 'firebase/firestore';
import LevelingSystem from '../SRS/LevelingSystem';
import { fetchManualSnippetCreationQuota, messageSnippetCreationMonthlyLimitReached, messageForSnippetQuotaCheckUnavailable } from '../../services/subscriptionQuotaService';
import CodeEditor from '../CodeEditor/CodeEditor';
import './CardManager.css';

const CardManager = ({ userId, deckId, onCardAdded, editingCard = null }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [solution, setSolution] = useState('');
  const [tags, setTags] = useState('');
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [levelingSystem] = useState(new LevelingSystem(userId));

  useEffect(() => {
    if (editingCard) {
      setTitle(editingCard.title);
      setDescription(editingCard.description);
      setSolution(editingCard.solution);
      setTags(editingCard.tags ? editingCard.tags.join(', ') : '');
      setIsEditing(true);
    } else {
      resetForm();
    }
  }, [editingCard]);

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setSolution('');
    setTags('');
    setIsEditing(false);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim() || !description.trim() || !solution.trim()) {
      setError('Please fill in all fields');
      return;
    }

    try {
      if (isEditing && editingCard) {
        // Update existing card
        const cardRef = doc(db, `users/${userId}/decks/${deckId}/cards/${editingCard.id}`);
        await updateDoc(cardRef, {
          title: title.trim(),
          description: description.trim(),
          solution: solution.trim(),
          tags: tags.split(',').map(tag => tag.trim()).filter(tag => tag),
          updatedAt: new Date().toISOString()
        });
      } else {
        const quota = await fetchManualSnippetCreationQuota();
        if (quota.quotaCheckUnavailable) {
          setError(messageForSnippetQuotaCheckUnavailable(quota));
          return;
        }
        if (!quota.isPremium && quota.allowed === false) {
          setError(messageSnippetCreationMonthlyLimitReached(quota));
          return;
        }

        // Create new card
        const cardsRef = collection(db, `users/${userId}/decks/${deckId}/cards`);

        // Initialize leveling fields
        const levelingFields = levelingSystem.initializeSnippetLeveling();

        await addDoc(cardsRef, {
          title: title.trim(),
          description: description.trim(),
          solution: solution.trim(),
          creationSource: 'manual',
          tags: tags.split(',').map(tag => tag.trim()).filter(tag => tag),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          // Add leveling fields
          level: levelingFields.level,
          current_exp: levelingFields.current_exp,
          exp_to_level: levelingFields.exp_to_level,
          currentLineIndex: 0
        });
      }

      resetForm();
      if (onCardAdded) {
        onCardAdded();
      }
    } catch (error) {
      console.error('Error saving card:', error);
      setError(`Failed to ${isEditing ? 'update' : 'add'} card. Please try again.`);
    }
  };

  return (
    <div className="card-manager-deck-embedded">
      <p className="card-manager-deck-embedded__lede">
        {isEditing ? "Update this card's prompt, solution, and tags." : 'Add a new snippet to this cookbook.'}
      </p>

      {error && (
        <div className="card-manager-deck-embedded__error error-message" role="alert">
          {error}
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="full-page-form full-page-form--flashcard-embedded card-manager-deck-embedded__form"
      >
        <div className="form-group">
          <label htmlFor="card-manager-title">Title <span className="required-field">*</span></label>
          <input
            type="text"
            id="card-manager-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., Python list comprehension"
            className="divider-input"
            autoComplete="off"
          />
        </div>

        <div className="form-group">
          <label htmlFor="card-manager-description">Description <span className="required-field">*</span></label>
          <textarea
            id="card-manager-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Explain the concept or give instructions for the snippet…"
            className="divider-input"
            rows={3}
          />
        </div>

        <div className="form-group">
          <label htmlFor="card-manager-tags">Tags (comma-separated)</label>
          <input
            type="text"
            id="card-manager-tags"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="e.g., arrays, loops"
            className="divider-input"
            autoComplete="off"
          />
        </div>

        <div className="form-group card-manager-deck-embedded__solution-field">
          <span className="card-manager-deck-embedded__solution-label">
            Solution <span className="required-field">*</span>
          </span>
          <CodeEditor
            code={solution}
            onChange={setSolution}
            placeholder="Write the solution code here…"
          />
        </div>

        <div className="form-action-center">
          <button type="submit" className="submit-create-button">
            {isEditing ? 'Update snippet' : 'Add snippet'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CardManager;
